/**
 * Bilt Passport — shared copy and label helpers.
 *
 * Premium/editorial tone, honest about proof strength. No XP/points/unlock
 * language anywhere in this module or its consumers.
 */
import type {
  PassportEncounterMethod,
  PassportEncounterStatus,
  PassportRiskStatus,
  PassportSubjectType,
  PassportVerificationLevel,
} from '../../services/api/passport';

export const VERIFICATION_LEVEL_LABELS: Record<PassportVerificationLevel, string> = {
  basic_qr: 'Basic QR',
  time_verified: 'Time-window verified',
  event_verified: 'Event verified',
  proximity_verified: 'Proximity verified',
  staff_verified: 'Staff verified',
  offline_pending: 'Pending validation',
  rejected_suspicious: 'Not verified',
};

export const STATUS_LABELS: Record<PassportEncounterStatus, string> = {
  pending: 'Pending validation',
  verified: 'Verified',
  rejected: 'Not verified',
  revoked: 'Revoked',
};

export const SUBJECT_TYPE_LABELS: Record<PassportSubjectType, string> = {
  vehicle: 'Vehicle',
  event: 'Event',
  shop: 'Shop',
  club: 'Club',
  booth: 'Booth',
  activation: 'Activation',
  destination: 'Destination',
  checkpoint: 'Checkpoint',
};

export const METHOD_LABELS: Record<PassportEncounterMethod, string> = {
  qr_scan: 'QR scan',
  nfc_tap: 'NFC tap',
  manual_code: 'Manual code',
  geofence_checkin: 'Location check-in',
  organizer_grant: 'Organizer grant',
  system_grant: 'Platform grant',
};

export function verificationLevelLabel(level: PassportVerificationLevel | undefined | null): string {
  if (!level) return 'Unverified';
  return VERIFICATION_LEVEL_LABELS[level] || level;
}

export function encounterStatusLabel(status: PassportEncounterStatus | undefined | null): string {
  if (!status) return 'Unknown';
  return STATUS_LABELS[status] || status;
}

/** Tone for status badges — never color-only; always paired with text. */
export function encounterStatusTone(status: PassportEncounterStatus): 'amber' | 'emerald' | 'red' | 'neutral' {
  switch (status) {
    case 'verified':
      return 'emerald';
    case 'pending':
      return 'amber';
    case 'rejected':
    case 'revoked':
      return 'red';
    default:
      return 'neutral';
  }
}

export function riskStatusLabel(risk: PassportRiskStatus | undefined | null): string {
  switch (risk) {
    case 'flagged':
      return 'Flagged for review';
    case 'held':
      return 'Held for review';
    default:
      return 'Clear';
  }
}

export function formatEncounterDate(value: string | null | undefined): string {
  if (!value) return '—';
  const d = new Date(value.replace(' ', 'T'));
  if (Number.isNaN(d.getTime())) return value;
  return d.toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' });
}

export function formatEncounterDateTime(value: string | null | undefined): string {
  if (!value) return '—';
  const d = new Date(value.replace(' ', 'T'));
  if (Number.isNaN(d.getTime())) return value;
  return d.toLocaleString(undefined, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
}

export const PASSPORT_DISABLED_TITLE = "Bilt Passport isn't available yet";
export const PASSPORT_DISABLED_DESCRIPTION =
  "We're building a private, honest record of the builds and events you've seen in person. Check back soon.";
